import {
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { createUserSocket } from "../utils/userSocket";
import { resetCart } from "../redux/cartRedux";
import { resetOrderCreation } from "../redux/restaurantOrderActions"; // Adjust the import path if needed
import { resetOrderCreation as resetGeneralOrderCreation } from "../redux/createOrderAction";
import {
  resetOrderCreation as resetBrandOrderCreation,
  resetBrandOrder,
} from "../redux/brandOrderActions";
import { resetShippingCost } from "../redux/shippingCostRedux";
import { clearCart } from "../redux/brandCartSlice";
import useBlockBackNavigation from "../hooks/useBlockBackNavigation";
import { pickLatestOrder } from "../utils/pickLatestOrder";
import Navbar from "../components/Navbar";
import OrderConfirmation from "../components/Order/OrderConfirmation";

// ─────────────────────────────────────────────────────────────────────────────
// Rams-optimized COD order summary (same shell as the redesigned Confirm page):
// narrow centered column, success-first hierarchy, borderless item rows, quiet
// details, sticky total + Continue Shopping. PRESENTATION ONLY — order-source
// resolution, fee/total math, SSE tracking and the continue-shopping resets are
// unchanged.
// ─────────────────────────────────────────────────────────────────────────────

const CODOrderScreen = () => {
  const user = useSelector((state) => state.user.currentUser);
  const userId = user?.result?._id || user?._id;

  // Access the order from multiple sources (restaurant, brand, deals, etc.)
  const generalOrder = useSelector((state) => state.orders.order);
  const restaurantOrder = useSelector((state) => state.restaurantOrder.order);
  const brandOrder = useSelector((state) => state.brandOrder.order);
  const retailerWholesalerOrder = useSelector(
    (state) => state.retailerWholesalerOrder?.order
  );
  const healthcareOrder = useSelector((state) => state.healthcareOrder?.order);

  /* Whichever of these was created LAST is the order the customer just placed.
     The old fixed chain put brandOrder ahead of everything, and these slices
     are not cleared between checkouts, so a restaurant order placed after a
     brand order in the same session showed the brand order here — wrong id,
     wrong items, wrong total. Candidates stay in the legacy order so an
     undated slice still falls back to the previous precedence. */
  const order =
    pickLatestOrder([
      generalOrder,
      brandOrder,
      restaurantOrder,
      retailerWholesalerOrder,
      healthcareOrder,
    ]) || {};

  // Show the total the server actually charged for THIS order. The created order
  // carries the authoritative distance-based fee/total; the legacy flat
  // `/shippingCost` slice is no longer used for the charged amount (it was a
  // fixed zone fee unrelated to the fee on this order). Prefer order.totalPrice,
  // falling back to itemsPrice + order.deliveryFee when an older order shape lacks it.

  const dispatch = useDispatch();
  const history = useHistory();

  // Back must not return into the checkout flow (double-order protection).
  useBlockBackNavigation("/");

  const handleContinueShopping = () => {
    dispatch(resetCart());
    dispatch(resetOrderCreation());
    dispatch(resetGeneralOrderCreation());
    dispatch(resetBrandOrderCreation());
    dispatch(resetBrandOrder());
    // The cart to clear belongs to the order actually on screen, not to
    // whatever brand order happened to be left in the slice.
    dispatch(clearCart(order?.vendorId || null));
    dispatch(resetShippingCost());
    setTrackOrder({});
    history.push("/my_profile");  // Navigate to MyProfile to view the newly created order
  };


  // Active Step

  // getStepContent

  const [trackOrder, setTrackOrder] = useState();
  const orderId = order?._id;

  // ── Live order tracking over Socket.IO ────────────────────────────────────
  // Replaces the legacy `/mobile/:userId/stream` SSE. That endpoint queried the
  // customer's LATEST order ONCE at connect time and then re-sent the same
  // stale snapshot every 10 s — fresh statuses only ever arrived because this
  // effect used to depend on `trackOrder` and tore the connection down after
  // every message (a reconnect + DB query every 10 s, per viewer). It could
  // also show a DIFFERENT order's status if a newer order existed.
  //
  // Now: same infra as MyProfile — join `orderRoom:${client_id}`; the backend
  // Mongo change stream emits `orderUpdate` (full order doc) to that room on
  // every order write; we act ONLY on updates for THIS order.
  useEffect(() => {
    if (!userId || !orderId) return;

    // Carries the customer's token: their rooms need it (utils/userSocket.js).
    const socket = createUserSocket();

    socket.on("connect", () => {
      console.log("[CODOrder] Socket connected, joining order room…");
      socket.emit("join", `orderRoom:${userId}`);
    });

    socket.on("orderUpdate", (updatedOrder) => {
      // The room carries ALL of this customer's orders — track only this one.
      if (String(updatedOrder?._id) !== String(orderId)) return;
      console.log(
        "[CODOrder] orderUpdate:",
        updatedOrder?._id,
        updatedOrder?.delivery_status
      );
      setTrackOrder(updatedOrder);
    });

    socket.on("disconnect", () => {
      console.log("[CODOrder] Socket disconnected");
    });

    return () => {
      socket.off("orderUpdate");
      socket.disconnect();
    };
  }, [userId, orderId]);


  // ── Display helpers (presentation only) ──────────────────────────────────
  // Rams-honest tracking: don't show "Cancelled" as an upcoming step — it only
  // appears when the order actually IS cancelled/rejected.

  return (
    <>
      <Navbar />
      <OrderConfirmation
        /* Live socket doc when we have one, else the created order.
           trackOrder starts as {} and is replaced on `orderUpdate`, so
           gate on _id rather than truthiness. */
        order={trackOrder?._id ? trackOrder : order}
        paymentLabel={"Cash on delivery"}
        onContinueShopping={handleContinueShopping}
      />
    </>
  );
};

export default CODOrderScreen;
